import { useCallback, useEffect, useRef, useState } from 'react';

/** One page of a list, as every list route of the area answers it. */
export type PageOf<Row> = {
  readonly items: readonly Row[];
  readonly total: number;
  readonly nextCursor: string | null;
};

export type PagedState<Row, P extends PageOf<Row>> =
  | { readonly status: 'loading'; readonly items: readonly Row[] }
  | {
      readonly status: 'ready';
      readonly items: readonly Row[];
      /** The last page read: its totals and any field the route adds beside the rows. */
      readonly page: P;
      readonly hasMore: boolean;
      readonly loadingMore: boolean;
    }
  | {
      readonly status: 'error';
      readonly items: readonly Row[];
      readonly code: unknown;
      readonly httpStatus: number | undefined;
    };

type Answer<P> = {
  readonly data?: P | undefined;
  readonly error?: unknown;
  readonly response: Response;
};

function readyState<Row, P extends PageOf<Row>>(
  page: P,
  items: readonly Row[],
): PagedState<Row, P> {
  return { status: 'ready', items, page, hasMore: page.nextCursor !== null, loadingMore: false };
}

/**
 * A list read page by page, with «Mostra altri» through the cursor. `read` is the caller's, memoized
 * on its filters: a new `read` starts a new first page. Every first page starts a new generation,
 * so a «Mostra altri» page asked for an earlier one is dropped when it answers instead of being
 * appended to a list it no longer belongs to.
 */
export function usePagedList<Row, P extends PageOf<Row>>(
  read: (cursor: string | null) => Promise<Answer<P>>,
): {
  readonly state: PagedState<Row, P>;
  readonly more: () => void;
  readonly reload: () => void;
} {
  const [state, setState] = useState<PagedState<Row, P>>({ status: 'loading', items: [] });
  const [cursor, setCursor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    let cancelled = false;
    setState({ status: 'loading', items: [] });
    setCursor(null);
    void read(null).then(
      ({ data, error, response }) => {
        if (cancelled) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', items: [], code: error, httpStatus: response.status });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, data.items));
      },
      () => {
        if (!cancelled) {
          setState({ status: 'error', items: [], code: null, httpStatus: undefined });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [read, attempt]);

  const more = useCallback(() => {
    if (state.status !== 'ready' || cursor === null || state.loadingMore) {
      return;
    }
    const before = state;
    const asked = generation.current;
    setState({ ...before, loadingMore: true });
    void read(cursor).then(
      ({ data, error, response }) => {
        if (generation.current !== asked) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({
            status: 'error',
            items: before.items,
            code: error,
            httpStatus: response.status,
          });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, [...before.items, ...data.items]));
      },
      () => {
        if (generation.current === asked) {
          setState({ status: 'error', items: before.items, code: null, httpStatus: undefined });
        }
      },
    );
  }, [state, cursor, read]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, more, reload };
}
